// src/utils/cartCalculator.js
/**
 * CartCalculator.js
 * Single source of truth for all cart price calculations
 */

/**
 * Calculate the total price of all modifiers
 * @param {Array} modifiers - Array of modifier objects with price property
 * @returns {number} Total price of all modifiers
 */
export const calculateModifiersTotal = (modifiers = []) => {
  if (!Array.isArray(modifiers) || modifiers.length === 0) return 0;

  return modifiers.reduce((total, modifier) => {
    const price = modifier.price || modifier.amount || 0;
    return total + price;
  }, 0);
};

/**
 * Calculate the base price for an item
 * @param {Object} item - Item object
 * @returns {number} Base price of the item
 */
export const calculateBasePrice = (item) => {
  if (!item) return 0;

  // Check for direct price properties
  if (typeof item.price === 'number') return item.price;
  if (item.baseItem && typeof item.baseItem.price === 'number')
    return item.baseItem.price;
  if (item.originalItem && typeof item.originalItem.price === 'number')
    return item.originalItem.price;

  return 0;
};

/**
 * Calculate the total price of an item including modifiers
 * @param {Object} item - Item object
 * @param {Array} modifiers - Array of modifier objects (optional if item already has modifiers)
 * @returns {number} Total price of the item with modifiers
 */
export const calculateItemPrice = (item, modifiers = null) => {
  if (!item) return 0;

  // Check if the item already has a pre-calculated price
  if (
    item.displayPrice &&
    item.displayPrice.includes('$') &&
    typeof item.price === 'number'
  ) {
    return item.price;
  }

  const basePrice = calculateBasePrice(item);

  // Use provided modifiers, or try to find them on the item
  const itemModifiers =
    modifiers ||
    item.modifiers ||
    item.modifications ||
    (item.originalItem ? item.originalItem.modifiers : []) ||
    [];

  const modifiersTotal = calculateModifiersTotal(itemModifiers);

  return basePrice + modifiersTotal;
};

/**
 * Calculate the subtotal for an item (price × quantity)
 * @param {Object} item - Item object with price and quantity
 * @param {Array} modifiers - Array of modifier objects (optional)
 * @returns {number} Subtotal for the item
 */
export const calculateSubtotal = (item, modifiers = null) => {
  if (!item) return 0;

  const quantity = item.quantity || 1;
  const itemPrice = calculateItemPrice(item, modifiers);

  return itemPrice * quantity;
};

/**
 * Calculate the total for all items in the cart
 * @param {Array} cartItems - Array of cart items
 * @returns {number} Total price for all items
 */
export const calculateCartTotal = (cartItems = []) => {
  if (!Array.isArray(cartItems) || cartItems.length === 0) return 0;

  return cartItems.reduce((total, item) => {
    return total + calculateSubtotal(item);
  }, 0);
};

/**
 * Format a price as a currency string
 * @param {number} price - Price to format
 * @returns {string} Formatted price string (e.g., "$10.99")
 */
export const formatPrice = (price) => {
  if (price === undefined || price === null) return 'N/A';
  const formatted = `$${Number(price).toFixed(2)}`;
  return formatted;
};
